/**
 * Field builder functions using SOLID principles
 * Each builder function creates a field definition with proper formatters
 */

import { FIELD_TYPES } from '../fieldDefinitions.js';
import {
  getTextFormatters,
  getTextareaFormatters,
  getSelectFormatters
} from '../formatters/index.js';
import {
  createGpsDisplayFormatter,
  createGpsExifFormatters,
  createFractionFormatters,
  createNumberFormatters
} from '../formatters/numberFormatters.js';
import { getDateFormatters } from '../formatters/dateFormatters.js';
import { getShutterSpeedFormatters } from '../formatters/shutterSpeedFormatters.js';

// ==================== Text Field Builder ====================

/**
 * Creates a text field definition
 * @param {Object} config - Field configuration
 */
export function buildTextField(config) {
  const { id, label, category, exifPath, exifTag, options = {} } = config;
  return {
    id,
    label,
    category,
    type: FIELD_TYPES.TEXT,
    exifPath,
    exifTag,
    supportsPresets: true,
    ...getTextFormatters(options),
    ...(options.presetGroup && { presetGroup: options.presetGroup }),
    ...(options.displayGroup && { displayGroup: options.displayGroup })
  };
}

// ==================== Textarea Field Builder ====================

/**
 * Creates a textarea field definition
 * @param {Object} config - Field configuration
 */
export function buildTextareaField(config) {
  const { id, label, category, exifPath, exifTag, options = {} } = config;
  return {
    id,
    label,
    category,
    type: FIELD_TYPES.TEXTAREA,
    exifPath,
    exifTag,
    supportsPresets: true,
    ...getTextareaFormatters(options),
    ...(options.presetGroup && { presetGroup: options.presetGroup }),
    ...(options.displayGroup && { displayGroup: options.displayGroup })
  };
}

// ==================== Select Field Builder ====================

/**
 * Creates a select field definition
 * @param {Object} config - Field configuration
 */
export function buildSelectField(config) {
  const { id, label, category, exifPath, exifTag, options, formatterOptions = {} } = config;
  return {
    id,
    label,
    category,
    type: FIELD_TYPES.SELECT,
    exifPath,
    exifTag,
    supportsPresets: true,
    ...getSelectFormatters(options, formatterOptions)
  };
}

// ==================== GPS Coordinate Field Builder ====================

/**
 * Creates a GPS coordinate field definition (latitude or longitude)
 * @param {Object} config - Field configuration
 */
export function buildGpsCoordinateField(config) {
  const { id, label, category, exifTag, exifRefTag } = config;
  return {
    id,
    label,
    category,
    type: FIELD_TYPES.GPS_COORDINATE,
    exifPath: 'GPS',
    exifTag,
    exifRefTag,
    supportsPresets: true,
    presetGroup: 'gps',
    displayGroup: 'coordinates',
    formatForDisplay: createGpsDisplayFormatter(),
    ...createGpsExifFormatters(exifRefTag.positiveValue, exifRefTag.negativeValue)
  };
}

// ==================== Number Field Builders ====================

/**
 * Creates a number field stored as fraction in EXIF (e.g., aperture, focal length)
 * @param {Object} config - Field configuration
 */
export function buildFractionNumberField(config) {
  const { id, label, category, exifPath, exifTag, multiplier = 10, suffix = '' } = config;
  return {
    id,
    label,
    category,
    type: FIELD_TYPES.NUMBER,
    exifPath,
    exifTag,
    supportsPresets: true,
    ...createFractionFormatters(multiplier, suffix)
  };
}

/**
 * Creates a simple number field with display prefix/suffix
 * @param {Object} config - Field configuration
 */
export function buildNumberField(config) {
  const { id, label, category, exifPath, exifTag, displayOptions = {} } = config;
  return {
    id,
    label,
    category,
    type: FIELD_TYPES.NUMBER,
    exifPath,
    exifTag,
    supportsPresets: true,
    ...createNumberFormatters(displayOptions)
  };
}

// ==================== Date Field Builder ====================

/**
 * Creates a date/time field definition
 * @param {Object} config - Field configuration
 */
export function buildDateField(config) {
  const { id, label, category, exifPath, exifTag, exifSecondaryTags } = config;
  const field = {
    id,
    label,
    category,
    type: FIELD_TYPES.DATE,
    exifPath,
    exifTag,
    supportsPresets: true,
    ...getDateFormatters()
  };

  if (exifSecondaryTags) {
    field.exifSecondaryTags = exifSecondaryTags;
  }

  return field;
}

// ==================== Shutter Speed Field Builder ====================

/**
 * Creates a shutter speed field definition (special text field with fraction handling)
 * @param {Object} config - Field configuration
 */
export function buildShutterSpeedField(config) {
  const { id, label, category, exifPath, exifTag } = config;
  return {
    id,
    label,
    category,
    type: FIELD_TYPES.TEXT,
    exifPath,
    exifTag,
    supportsPresets: true,
    ...getShutterSpeedFormatters()
  };
}

// ==================== Altitude Field Builder ====================

/**
 * Creates an altitude field definition (GPS altitude with ref tag)
 * @param {Object} config - Field configuration
 */
export function buildAltitudeField(config) {
  const { id, label, category, exifTag, exifRefTag } = config;
  return {
    id,
    label,
    category,
    type: FIELD_TYPES.NUMBER,
    exifPath: 'GPS',
    exifTag,
    exifRefTag,
    supportsPresets: true,
    formatForDisplay: (value) => value != null ? `${value}m` : '-',
    formatForExif: (value) => {
      if (value === null || value === undefined) return null;
      return {
        altitude: [Math.abs(value), 1],
        ref: value >= 0 ? 0 : 1
      };
    },
    formatFromExif: (altitude, ref) => {
      if (!altitude) return null;
      const value = altitude[0] / altitude[1];
      return ref === 1 ? -value : value;
    }
  };
}
